import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { Star } from "@/components/landing/Logo";
import { useLang } from "@/i18n/LanguageContext";

const T = {
  de: { kicker: "Mira in Bewegung", title: "Sieh Mira im Einsatz", sub: "Kurze Clips, wie deine ehrlichste Freundin arbeitet – tippe für Ton.", pick: "Clip auswählen", labels: ["Outfit-Check zu zweit", "POV: Der ehrliche Spiegel", "Mira – der Spiegel, der ehrlich ist", "Schaufenster-Look an dir"], b2bKicker: "Video", b2bTitle: "Mira für deine Boutique – in 36 Sekunden", b2bSub: "Du musst den Online-Handel nicht fürchten: So bringt Mira Beratung, Anprobe und neue Gründe reinzukommen auf deine Fläche." },
  en: { kicker: "Mira in motion", title: "See Mira in action", sub: "Short clips of your most honest friend at work – tap for sound.", pick: "Choose a clip", labels: ["Outfit check with a friend", "POV: The honest mirror", "Mira – the mirror that's honest", "Window look on you"], b2bKicker: "Video", b2bTitle: "Mira for your boutique – in 36 seconds", b2bSub: "No need to fear online retail: how Mira brings advice, try-ons and new reasons to come in to your store. (German audio/text)" },
  es: { kicker: "Mira en movimiento", title: "Mira en acción", sub: "Clips breves de tu amiga más sincera – toca para el sonido.", pick: "Elige un clip", labels: ["Outfit-check entre amigas", "POV: El espejo sincero", "Mira – el espejo que es sincero", "Look de escaparate en ti"], b2bKicker: "Vídeo", b2bTitle: "Mira para tu boutique – en 36 segundos", b2bSub: "No temas al comercio online: así lleva Mira asesoramiento, prueba virtual y nuevas razones para entrar a tu tienda. (Vídeo en alemán)" },
  it: { kicker: "Mira in movimento", title: "Mira in azione", sub: "Brevi clip della tua amica più sincera – tocca per l'audio.", pick: "Scegli una clip", labels: ["Outfit check tra amiche", "POV: Lo specchio sincero", "Mira – lo specchio che è sincero", "Look da vetrina su di te"], b2bKicker: "Video", b2bTitle: "Mira per la tua boutique – in 36 secondi", b2bSub: "Non temere l'e-commerce: così Mira porta consulenza, prova virtuale e nuovi motivi per entrare nel tuo negozio. (Video in tedesco)" },
  fr: { kicker: "Mira en mouvement", title: "Mira en action", sub: "Courts clips de ta copine la plus honnête – touche pour le son.", pick: "Choisis un clip", labels: ["Outfit check entre copines", "POV : Le miroir honnête", "Mira – le miroir qui est honnête", "Look de vitrine sur toi"], b2bKicker: "Vidéo", b2bTitle: "Mira pour ta boutique – en 36 secondes", b2bSub: "Pas besoin de craindre le commerce en ligne : Mira apporte conseil, essayage virtuel et de nouvelles raisons d'entrer en boutique. (Vidéo en allemand)" },
};

const ORIGIN = "https://mira-mirror.ai";

const VIDEO_META = {
  "mira-reel-duo": { name: "Mira Outfit-Check: Zwei Freundinnen, ein ehrliches Urteil", desc: "Kurzes Reel: Wie Mira per Foto liebevoll ehrlich sagt, welches Outfit dich strahlen lässt.", dur: "PT25S" },
  "mira-reel-pov": { name: "POV: Dein Spiegel sagt dir endlich die Wahrheit", desc: "POV-Reel: Mira analysiert dein Outfit liebevoll ehrlich – Farben, Passform, Wirkung.", dur: "PT28S" },
  "mira-reel-social": { name: "Mira – der Spiegel, der ehrlich ist", desc: "Der KI-Spiegel in Aktion: Begrüssung, ehrliche Analyse und virtuelle Anprobe in 30 Sekunden.", dur: "PT30S" },
  "mira-boutique": { name: "Der Mira-Zauberspiegel für Boutiquen in 30 Sekunden", desc: "So erlebt deine Kundin Mira im Laden: scannen, virtuell anprobieren, ehrliche Empfehlung – mehr Umsatz pro Besuch.", dur: "PT30S" },
  "mira-b2b-intro": { name: "Mira für Boutiquen: Das Einführungsvideo in 36 Sekunden", desc: "Warum dein Laden kann, was der Online-Handel nicht kann – und wie Mira Beratung, virtuelle Anprobe und Schaufenster-Looks auf deine Fläche bringt.", dur: "PT36S" },
  "schaufenster-reel": { name: "Schaufenster-Looks: Der Look vom Mannequin – an dir", desc: "Kurzes Reel: Mannequin einmal fotografieren – jede Kundin trägt den kompletten Schaufenster-Look per Tap an sich selbst.", dur: "PT20S" },
};

const DE_ONLY = new Set(["mira-b2b-intro"]);

const videoObject = (key, lang) => {
  const en = lang !== "de" && !DE_ONLY.has(key);
  return {
    "@type": "VideoObject",
    name: VIDEO_META[key].name,
    description: VIDEO_META[key].desc,
    thumbnailUrl: `${ORIGIN}/videos/${key}-poster.webp`,
    contentUrl: `${ORIGIN}/videos/${key}${en ? "-en" : ""}.mp4`,
    duration: VIDEO_META[key].dur,
    uploadDate: "2026-06-14T09:00:00+02:00",
    inLanguage: en ? "en" : "de",
    publisher: { "@type": "Organization", name: "Mira Mirror" },
  };
};

const useVideoLd = (id, keys, lang) => {
  useEffect(() => {
    document.getElementById(id)?.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = id;
    const objs = keys.map((k) => ({ "@context": "https://schema.org", ...videoObject(k, lang) }));
    ld.text = JSON.stringify(objs.length === 1 ? objs[0] : objs);
    document.head.appendChild(ld);
    return () => document.getElementById(id)?.remove();
  }, [id, keys, lang]);
};

const REELS = ["/videos/mira-reel-duo", "/videos/mira-reel-pov", "/videos/mira-reel-social", "/videos/schaufenster-reel"];
const REEL_KEYS = ["mira-reel-duo", "mira-reel-pov", "mira-reel-social", "schaufenster-reel"];
const B2B_KEYS = ["mira-b2b-intro"];

const langSrc = (base, lang) => (lang === "de" ? `${base}.mp4` : `${base}-en.mp4`);

const ActiveReel = ({ base, lang }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [src, setSrc] = useState(langSrc(base, lang));

  useEffect(() => { setSrc(langSrc(base, lang)); setMuted(true); }, [base, lang]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [src]);

  return (
    <div key={base} className="reel-fade relative overflow-hidden rounded-[1.8rem] border-2 shadow-2xl" style={{ borderColor: "var(--gold)", background: "#000", aspectRatio: "9/16" }} data-testid="video-reel-active">
      <video
        ref={ref}
        src={src}
        poster={`${base}-poster.webp`}
        muted={muted}
        autoPlay
        loop
        playsInline
        preload="metadata"
        className="h-full w-full object-cover"
        onClick={() => setMuted((m) => !m)}
        onError={() => { if (src !== `${base}.mp4`) setSrc(`${base}.mp4`); }}
      />
      <button
        onClick={() => setMuted((m) => !m)}
        data-testid="video-reel-mute"
        aria-label={muted ? "Ton an" : "Ton aus"}
        className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full text-white backdrop-blur-md transition-transform duration-300 hover:scale-110"
        style={{ background: "rgba(44,39,37,0.55)" }}
      >
        {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
      </button>
    </div>
  );
};

const ReelThumb = ({ base, label, onClick, idx }) => (
  <button
    onClick={onClick}
    data-testid={`video-reel-thumb-${idx}`}
    className="group relative w-full overflow-hidden rounded-[1.3rem] border-2 text-left shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    style={{ borderColor: "var(--line)", background: "#000", aspectRatio: "9/16" }}
  >
    <img src={`${base}-poster.webp`} alt={label} loading="lazy" className="h-full w-full object-cover opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full text-white backdrop-blur-md transition-transform duration-300 group-hover:scale-110" style={{ background: "rgba(44,39,37,0.55)" }}>
        <Play size={18} fill="currentColor" />
      </span>
    </span>
    <span className="absolute inset-x-0 bottom-0 px-3 pb-2.5 pt-8 text-[11px] font-semibold leading-snug text-white" style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.65))" }}>
      {label}
    </span>
  </button>
);

export const VideoReels = () => {
  const { lang } = useLang();
  const x = T[lang] || T.de;
  useVideoLd("ld-videos-b2c", REEL_KEYS, lang);
  const [active, setActive] = useState(0);
  const others = [0, 1, 2, 3].filter((i) => i !== active);
  return (
    <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8" data-testid="video-reels-section">
      <Reveal>
        <p className="flex items-center gap-2 text-[11px] font-black uppercase" style={{ letterSpacing: "0.24em", color: "var(--camel)" }}>
          <Star size={11} /> {x.kicker}
        </p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold" data-testid="video-reels-title">{x.title}</h2>
          <p className="max-w-sm text-sm" style={{ color: "var(--muted-warm)" }}>{x.sub}</p>
        </div>
      </Reveal>
      <Reveal className="mt-9">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 sm:flex-row sm:items-stretch sm:justify-center sm:gap-7">
          <div className="w-full max-w-[340px] sm:w-[55%] sm:max-w-[380px]">
            <ActiveReel key={REELS[active]} base={REELS[active]} lang={lang} />
            <p className="mt-3 text-center text-sm font-semibold" data-testid="video-reel-active-label">{x.labels[active]}</p>
          </div>
          <div className="flex w-full max-w-[340px] flex-row gap-3 sm:w-[30%] sm:max-w-[140px] sm:flex-col sm:justify-center">
            <p className="sr-only">{x.pick}</p>
            {others.map((i) => (
              <ReelThumb key={REELS[i]} base={REELS[i]} label={x.labels[i]} idx={i} onClick={() => setActive(i)} />
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export const BoutiqueVideo = () => {
  const { lang } = useLang();
  const x = T[lang] || T.de;
  useVideoLd("ld-video-b2b", B2B_KEYS, lang);
  const base = "/videos/mira-b2b-intro";
  const src = `${base}.mp4`;
  return (
    <section className="mt-20" data-testid="boutique-video-section">
      <Reveal>
        <p className="flex items-center gap-2 text-[11px] font-black uppercase" style={{ letterSpacing: "0.24em", color: "var(--camel)" }}>
          <Star size={11} /> {x.b2bKicker}
        </p>
        <h2 className="mt-3 font-serif-display text-3xl font-semibold" data-testid="boutique-video-title">{x.b2bTitle}</h2>
        <p className="mt-2 text-sm" style={{ color: "var(--muted-warm)" }}>{x.b2bSub}</p>
        <div className="mt-7 overflow-hidden rounded-[1.8rem] border-2 shadow-xl" style={{ borderColor: "var(--gold)", background: "#000" }}>
          <video
            src={src}
            poster={`${base}-poster.webp`}
            controls
            playsInline
            preload="metadata"
            className="w-full"
            style={{ aspectRatio: "16/9" }}
            data-testid="boutique-video-player"
          />
        </div>
      </Reveal>
    </section>
  );
};
